/**
 * «Памʼять агента» (ч.1363) — правила і вимова для ВСІХ роликів.
 *
 * Зауваження власника: «агент не памʼятає зробленого і щоразу робить ті самі помилки: я сказав,
 * як вимовляється IBAN, а він знову каже неправильно». Зауваження діяло лише на свій ролик.
 * Тепер:
 * - «Вимова» — слово, яке голос вимовляє інакше, ніж написано (IBAN → айбен); підставляється
 *   перед озвучкою кожного ролика, у субтитрах лишається написання;
 * - «Правила для агента» — агент читає їх перед кожним роликом і кожною правкою.
 * Нові записи агент додає сам із зауважень (видно, з якого ролика); редактор їх править,
 * вимикає, видаляє і додає свої.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { platformVideo, type AgentMemoryItem, type AgentMemoryKind } from '@/api/platformVideo';
import { extractApiError } from '@/utils/extractApiError';
import { NOTICE, fmtDT } from './PlatformVideoPage';

const HINT = { marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' } as const;

/** Звідки запис: ролик і зауваження (агент), хто додав, або початкове наповнення. */
function sourceTitle(item: AgentMemoryItem): string {
  const parts = [item.source_note || (item.author_name || item.author_email ? 'додано вручну' : '')];
  if (item.remark_text) parts.push(`Зауваження: ${item.remark_text}`);
  parts.push(`Змінено: ${fmtDT(item.updated_at)}`);
  return parts.filter(Boolean).join('\n');
}

function Source({ item }: { item: AgentMemoryItem }) {
  if (item.guide_id) {
    return (
      <span title={sourceTitle(item)}>
        {'агент: '}
        <Link to={`/platform/video/${item.guide_id}`}>{`«${item.guide_title || 'ролик'}»`}</Link>
      </span>
    );
  }
  if (item.author_name || item.author_email) {
    return <span title={sourceTitle(item)}>{`додано: ${item.author_name || item.author_email}`}</span>;
  }
  return <span title={sourceTitle(item)}>{item.source_note || '—'}</span>;
}

type Draft = { id: string; text: string; spoken: string };

export default function VideoMemoryTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-video-memory'], queryFn: platformVideo.memory });
  const settings = useQuery({ queryKey: ['platform-video-settings'], queryFn: platformVideo.settings });
  const [word, setWord] = useState('');
  const [spoken, setSpoken] = useState('');
  const [rule, setRule] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const done = (text: string) => {
    setErr(null); setMsg(text);
    qc.invalidateQueries({ queryKey: ['platform-video-memory'] });
  };
  const fail = (e: unknown) => { setMsg(null); setErr(extractApiError(e)); };
  const add = useMutation({
    mutationFn: (v: { kind: AgentMemoryKind; text: string; spoken?: string }) =>
      platformVideo.addMemory(v.kind, v.text, v.spoken),
    onSuccess: (_r, v) => {
      if (v.kind === 'SAY') { setWord(''); setSpoken(''); } else setRule('');
      done(v.kind === 'SAY'
        ? 'Додано. Вимова діє на наступні зйомки; щоб почути її в готовому ролику — «перезняти» на його сторінці.'
        : 'Додано. Агент дотримуватиметься правила в наступних роликах і правках.');
    },
    onError: fail,
  });
  const patch = useMutation({
    mutationFn: ({ id, ...change }: { id: string; text?: string; spoken?: string; active?: boolean }) =>
      platformVideo.patchMemory(id, change),
    onSuccess: () => { setDraft(null); done('Збережено.'); },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (id: string) => platformVideo.deleteMemory(id),
    onSuccess: () => done('Видалено.'),
    onError: fail,
  });
  const voice = settings.data?.settings;
  const canListen = voice?.voice_provider === 'elevenlabs' && !!voice.voice;
  const listen = useMutation({
    mutationFn: async (phrase: string) => {
      const url = await platformVideo.sample(voice!.voice, voice!.elevenlabs_model, voice!.speech_rate, phrase);
      const audio = new Audio(url);
      audio.addEventListener('ended', () => { try { URL.revokeObjectURL(url); } catch { /* немає в тестах */ } });
      try {
        await Promise.resolve(audio.play());
      } catch { /* браузер заборонив автовідтворення — звук не критичний */ }
    },
    onError: fail,
  });

  if (q.isLoading) return <p>Завантаження…</p>;
  const items = q.data?.items ?? [];
  const say = items.filter((i) => i.kind === 'SAY');
  const rules = items.filter((i) => i.kind === 'RULE');
  const activeRules = rules.filter((i) => i.active).length;
  const editing = (i: AgentMemoryItem) => draft?.id === i.id;
  const busy = patch.isPending || remove.isPending;

  const actions = (i: AgentMemoryItem) => (
    <span style={{ display: 'inline-flex', gap: '0.5rem', flexWrap: 'wrap' }}>
      {editing(i) ? (
        <>
          <button type="button" className="btn-link" disabled={busy}
            onClick={() => patch.mutate({ id: i.id, text: draft!.text, ...(i.kind === 'SAY' ? { spoken: draft!.spoken } : {}) })}>
            зберегти
          </button>
          <button type="button" className="btn-link" onClick={() => setDraft(null)}>скасувати</button>
        </>
      ) : (
        <button type="button" className="btn-link" disabled={busy}
          onClick={() => { setMsg(null); setDraft({ id: i.id, text: i.text, spoken: i.spoken ?? '' }); }}>
          змінити
        </button>
      )}
      {i.kind === 'SAY' && (
        <button type="button" className="btn-link" disabled={!canListen || listen.isPending}
          title={canListen ? undefined : 'Прослухати можна з голосом ElevenLabs (вкладка «Налаштування»)'}
          onClick={() => listen.mutate(editing(i) ? draft!.spoken : (i.spoken ?? ''))}>
          прослухати
        </button>
      )}
      <button type="button" className="btn-link" disabled={busy}
        onClick={() => { if (window.confirm('Видалити запис з памʼяті агента?')) remove.mutate(i.id); }}>
        видалити
      </button>
    </span>
  );
  const activeCol: ColumnDef<AgentMemoryItem> = {
    key: 'active', header: 'Діє', width: 60, align: 'center', sortValue: (i) => (i.active ? 1 : 0),
    cell: (i) => (
      <input type="checkbox" checked={i.active} disabled={busy} aria-label={`Діє: ${i.text}`}
        onChange={(e) => patch.mutate({ id: i.id, active: e.target.checked })} />
    ),
  };
  const sourceCol: ColumnDef<AgentMemoryItem> = {
    key: 'source', header: 'Звідки', width: 320, sortValue: (i) => i.created_at,
    cell: (i) => <Source item={i} />,
  };
  const sayColumns: ColumnDef<AgentMemoryItem>[] = [
    { key: 'text', header: 'Як написано', width: 180, sortValue: (i) => i.text.toLowerCase(),
      cell: (i) => (editing(i)
        ? <input value={draft!.text} maxLength={60} aria-label="Як написано"
            onChange={(e) => setDraft({ ...draft!, text: e.target.value })} />
        : <strong>{i.text}</strong>) },
    { key: 'spoken', header: 'Як вимовляти', width: 200,
      cell: (i) => (editing(i)
        ? <input value={draft!.spoken} maxLength={80} aria-label="Як вимовляти"
            onChange={(e) => setDraft({ ...draft!, spoken: e.target.value })} />
        : i.spoken) },
    activeCol, sourceCol,
    { key: 'actions', header: '', width: 230, cell: actions },
  ];
  const ruleRow = (i: AgentMemoryItem) => (
    <div key={i.id} data-testid="memory-rule"
      style={{ borderTop: '1px solid var(--border)', padding: '0.5rem 0', opacity: i.active ? 1 : 0.55 }}>
      {editing(i) ? (
        <textarea rows={3} value={draft!.text} maxLength={300} aria-label="Правило" style={{ width: '100%' }}
          onChange={(e) => setDraft({ ...draft!, text: e.target.value })} />
      ) : (
        <div style={{ whiteSpace: 'pre-wrap' }}>{i.text}</div>
      )}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.25rem',
        fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <label style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center', margin: 0 }}>
          <input type="checkbox" checked={i.active} disabled={busy} aria-label={`Діє: ${i.text}`}
            onChange={(e) => patch.mutate({ id: i.id, active: e.target.checked })} />
          діє
        </label>
        <Source item={i} />
        <span>{fmtDT(i.updated_at)}</span>
        {actions(i)}
      </div>
    </div>
  );

  return (
    <>
      <p style={HINT}>
        Те, що агент памʼятає між роликами. Із кожного зауваження він сам виносить сюди загальні уроки — як
        вимовляти слово, чого не повторювати, як натискати елемент — і дотримується їх у всіх наступних роликах
        і правках. Тут їх можна виправити, вимкнути чи видалити, а також додати свої.
      </p>
      {msg && <div role="status" style={{ ...NOTICE, margin: '0 0 0.75rem' }}>{msg}</div>}
      {err && <div className="error">{err}</div>}

      <h3>Вимова</h3>
      <p style={HINT}>
        Слова, які голос вимовляє інакше, ніж вони написані. У тексті диктора і в субтитрах лишається написання
        (IBAN), а голос каже так, як тут (айбен). Діє на озвучку кожного ролика, знятого після зміни.
      </p>
      <form className="doc-grid" style={{ alignItems: 'end' }}
        onSubmit={(e) => { e.preventDefault(); add.mutate({ kind: 'SAY', text: word.trim(), spoken: spoken.trim() }); }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="vm-word">Як написано</label>
          <input id="vm-word" value={word} maxLength={60} placeholder="IBAN"
            onChange={(e) => { setWord(e.target.value); setMsg(null); }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="vm-spoken">Як вимовляти</label>
          <input id="vm-spoken" value={spoken} maxLength={80} placeholder="айбен"
            onChange={(e) => { setSpoken(e.target.value); setMsg(null); }} />
        </div>
        <div style={{ gridColumn: 'span 4', display: 'flex', gap: '0.5rem', marginBottom: '0.85rem' }}>
          <button type="submit" className="primary" disabled={!word.trim() || !spoken.trim() || add.isPending}>
            додати слово
          </button>
          <button type="button" disabled={!canListen || !spoken.trim() || listen.isPending}
            title={canListen ? undefined : 'Прослухати можна з голосом ElevenLabs (вкладка «Налаштування»)'}
            onClick={() => listen.mutate(spoken.trim())}>
            прослухати
          </button>
        </div>
      </form>
      <DataTable rows={say} columns={sayColumns} rowKey={(i) => i.id} rowMuted={(i) => (i.active ? undefined : 0.5)}
        initialSortKey="text" emptyText="Слів ще немає" />

      <h3>Правила для агента</h3>
      <p style={HINT}>
        Агент читає ці правила перед кожним роликом і кожною правкою. Діє {activeRules} з{' '}
        {q.data?.max_rules ?? 60} можливих — довгий список розмиває увагу, тож схожі правила краще обʼєднати.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); add.mutate({ kind: 'RULE', text: rule.trim() }); }}>
        <div className="field">
          <label htmlFor="vm-rule">Нове правило</label>
          <textarea id="vm-rule" rows={2} value={rule} maxLength={300}
            placeholder="Наприклад: підсумок «Тепер ви знаєте…» — лише в останньому кроці ролика"
            onChange={(e) => { setRule(e.target.value); setMsg(null); }} />
        </div>
        <button type="submit" className="primary" disabled={rule.trim().length < 3 || add.isPending}>
          додати правило
        </button>
      </form>
      <div style={{ marginTop: '0.75rem' }}>
        {rules.length === 0 && <p style={HINT}>Правил ще немає</p>}
        {rules.map(ruleRow)}
      </div>
    </>
  );
}
